import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Trophy, Calendar, Target, Medal, ChevronDown, ChevronRight } from 'lucide-react';

import { useLeaderboard } from './leaderboard/useLeaderboard';
import { LeaderboardHeader } from './leaderboard/LeaderboardHeader';
import { StandingsSection } from './leaderboard/StandingsSection';
import { ScorersSection, AssistsSection } from './leaderboard/PlayerRankingSection';
import { MatchesSection } from './leaderboard/MatchesSection';

const SECTIONS = [
  { id: 'standings', labelIt: 'Classifica', labelEn: 'Standings', icon: Trophy },
  { id: 'scorers', labelIt: 'Marcatori', labelEn: 'Scorers', icon: Target },
  { id: 'assists', labelIt: 'Assist', labelEn: 'Assists', icon: Medal },
  { id: 'matches', labelIt: 'Partite', labelEn: 'Matches', icon: Calendar },
];

const Leaderboard = () => {
  const { i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const [showChampDropdown, setShowChampDropdown] = React.useState(false);

  const {
    championships, selectedChampId, setSelectedChampId,
    standings, topScorers, topAssists, weeklyScorers,
    loading, loadingData, activeSection, setActiveSection,
    selectedChamp, recentResults, upcomingMatches,
    fetchChampData,
  } = useLeaderboard();

  if (loading) {
    return (
      <div className="min-h-screen ept-bg-ambient flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient text-white">
      <LeaderboardHeader isItalian={isItalian} loadingData={loadingData} fetchChampData={fetchChampData} selectedChamp={selectedChamp} />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {championships.length === 0 ? (
          <div className="text-center py-20">
            <Trophy size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400 text-lg">{isItalian ? 'Nessun campionato attivo al momento' : 'No active championships at the moment'}</p>
          </div>
        ) : (
          <>
            {/* Championship Picker */}
            <div className="relative" data-testid="championship-selector">
              <button onClick={() => setShowChampDropdown(!showChampDropdown)} className="w-full bg-[#0a0d15] border border-white/10 p-4 flex items-center justify-between hover:border-[#00F0FF]/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-[#FFEA00]/20 flex items-center justify-center">
                    <Trophy size={20} className="text-[#FFEA00]" />
                  </div>
                  <div className="text-left">
                    <p className="font-heading text-base sm:text-lg uppercase tracking-wider font-bold">{selectedChamp?.name || '---'}</p>
                    <p className="text-xs text-slate-500">
                      {selectedChamp?.season || ''}{' '}
                      {selectedChamp?.status === 'completed'
                        ? <span className="text-[#00E676]">{isItalian ? 'Completato' : 'Completed'}</span>
                        : <span className="text-[#00F0FF]">{isItalian ? 'In Corso' : 'In Progress'}</span>
                      }
                    </p>
                  </div>
                </div>
                <ChevronDown size={20} className={`text-slate-400 transition-transform ${showChampDropdown ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showChampDropdown && (
                  <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} className="absolute z-20 w-full mt-1 bg-[#0a0d15] border border-white/10 shadow-xl overflow-hidden">
                    {championships.map((c) => (
                      <button key={c.championship_id} onClick={() => { setSelectedChampId(c.championship_id); setShowChampDropdown(false); }} className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-white/5 transition-colors ${c.championship_id === selectedChampId ? 'bg-[#FFEA00]/5 border-l-2 border-[#FFEA00]' : ''}`} data-testid={`champ-option-${c.championship_id}`}>
                        <Trophy size={16} className={c.championship_id === selectedChampId ? 'text-[#FFEA00]' : 'text-slate-500'} />
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.season}</p>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Section Tabs */}
            <div className="flex gap-1 bg-[#0a0d15] border border-white/10 p-1 overflow-x-auto">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setActiveSection(s.id)} className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${activeSection === s.id ? 'bg-[#FFEA00] text-black' : 'text-slate-400 hover:text-white hover:bg-white/5'}`} data-testid={`section-${s.id}`}>
                    <Icon size={14} />
                    {isItalian ? s.labelIt : s.labelEn}
                  </button>
                );
              })}
            </div>

            {loadingData ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                {activeSection === 'standings' && <StandingsSection standings={standings} isItalian={isItalian} />}
                {activeSection === 'scorers' && <ScorersSection topScorers={topScorers} weeklyScorers={weeklyScorers} isItalian={isItalian} />}
                {activeSection === 'assists' && <AssistsSection topAssists={topAssists} isItalian={isItalian} />}
                {activeSection === 'matches' && <MatchesSection recentResults={recentResults} upcomingMatches={upcomingMatches} isItalian={isItalian} />}
              </>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600">EPT Italy eSports League</p>
          <Link to="/login" className="text-xs text-[#00F0FF] hover:text-[#00F0FF]/80 flex items-center gap-1" data-testid="login-link">
            {isItalian ? 'Accedi al tuo account' : 'Sign in to your account'}
            <ChevronRight size={12} />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Leaderboard;
